import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface TrialStatus {
  isOnTrial: boolean;
  trialExpired: boolean;
  trialDaysLeft: number;
  trialEndsAt: string | null;
  loading: boolean;
}

export const useTrialStatus = (restaurant: any | null): TrialStatus => {
  const [status, setStatus] = useState<TrialStatus>({
    isOnTrial: false,
    trialExpired: false,
    trialDaysLeft: 0,
    trialEndsAt: null,
    loading: true,
  });

  useEffect(() => {
    if (!restaurant) {
      setStatus(prev => ({ ...prev, loading: false }));
      return;
    }

    const checkTrialStatus = async () => {
      // First check if there's an active subscription — if so, trial is irrelevant
      const { data: activeSub } = await supabase
        .from("subscriptions")
        .select("id, current_period_end")
        .eq("restaurant_id", restaurant.id)
        .eq("status", "active")
        .maybeSingle();

      // Has active subscription that hasn't expired — not on trial
      const isSubValid = activeSub && activeSub.current_period_end &&
        new Date(activeSub.current_period_end).getTime() > Date.now();

      if (isSubValid) {
        // Has active subscription — not on trial regardless of trial fields
        setStatus({ isOnTrial: false, trialExpired: false, trialDaysLeft: 0, trialEndsAt: null, loading: false });
        return;
      }

      const trialEndsAt = (restaurant as any).trial_ends_at;
      const trialStartedAt = (restaurant as any).trial_started_at;

      if (!trialStartedAt || !trialEndsAt) {
        setStatus({ isOnTrial: false, trialExpired: false, trialDaysLeft: 0, trialEndsAt: null, loading: false });
        return;
      }

      const now = new Date();
      const endsAt = new Date(trialEndsAt);
      const diffMs = endsAt.getTime() - now.getTime();
      const daysLeft = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
      const expired = diffMs <= 0;

      setStatus({
        isOnTrial: true,
        trialExpired: expired,
        trialDaysLeft: daysLeft,
        trialEndsAt,
        loading: false,
      });
    };

    checkTrialStatus();
  }, [restaurant]);

  return status;
};
